import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { inputCls, btnPrimary, btnGhost, btnDanger, formatErr } from "@/components/admin/ui";
import { SOCIAL_PLATFORMS } from "@/data/siteText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SocialManager = ({ token }) => {
    const [items, setItems] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirming, setConfirming] = useState(null);
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        axios
            .get(`${API}/site-settings`)
            .then((res) => setItems(res.data.socials.map((s) => ({ ...s, label: s.label || "" }))))
            .catch(() => setItems([]));
    }, []);

    const update = (index, key) => (e) =>
        setItems((list) => list.map((item, i) => (i === index ? { ...item, [key]: e.target.value } : item)));

    const move = (index, direction) => {
        const next = [...items];
        const to = direction === "up" ? index - 1 : index + 1;
        [next[index], next[to]] = [next[to], next[index]];
        setItems(next);
    };

    const save = async (list) => {
        setSaving(true);
        try {
            const payload = list.map((i) => ({ platform: i.platform, url: i.url, label: i.label || null }));
            const { data } = await axios.put(`${API}/admin/socials`, { items: payload }, { headers });
            setItems(data.map((s) => ({ ...s, label: s.label || "" })));
            toast.success("Footer links updated.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not save the links."));
        } finally {
            setSaving(false);
        }
    };

    if (items === null) {
        return <div className="rounded-2xl border border-amber-500/10 bg-zinc-900/40 h-40 animate-pulse" />;
    }

    return (
        <section data-testid="admin-social-tab">
            <div className="space-y-4">
                {items.map((item, index) => (
                    <div
                        key={index}
                        data-testid={`admin-social-row-${index}`}
                        className="rounded-2xl border border-amber-500/20 bg-zinc-900/60 backdrop-blur-md p-4 sm:p-5 grid sm:grid-cols-[180px_1fr] gap-3"
                    >
                        <select
                            value={item.platform}
                            onChange={update(index, "platform")}
                            data-testid={`admin-social-platform-${index}`}
                            className={`${inputCls} appearance-none`}
                        >
                            {SOCIAL_PLATFORMS.map((p) => (
                                <option key={p.value} value={p.value} className="bg-zinc-900">
                                    {p.label}
                                </option>
                            ))}
                        </select>
                        <input
                            value={item.url}
                            onChange={update(index, "url")}
                            placeholder="https://…"
                            data-testid={`admin-social-url-${index}`}
                            className={inputCls}
                        />
                        {item.platform === "other" && (
                            <input
                                value={item.label}
                                onChange={update(index, "label")}
                                placeholder="Label shown on hover"
                                data-testid={`admin-social-label-${index}`}
                                className={`${inputCls} sm:col-span-2`}
                            />
                        )}
                        <div className="sm:col-span-2 flex flex-wrap gap-2">
                            <button onClick={() => move(index, "up")} disabled={index === 0} data-testid={`admin-social-up-${index}`} className={`${btnGhost} px-3`} aria-label="Move up">
                                <ArrowUp size={13} />
                            </button>
                            <button onClick={() => move(index, "down")} disabled={index === items.length - 1} data-testid={`admin-social-down-${index}`} className={`${btnGhost} px-3`} aria-label="Move down">
                                <ArrowDown size={13} />
                            </button>
                            {confirming === index ? (
                                <>
                                    <button
                                        onClick={() => {
                                            const next = items.filter((_, i) => i !== index);
                                            setConfirming(null);
                                            setItems(next);
                                            save(next);
                                        }}
                                        data-testid={`admin-social-confirm-delete-${index}`}
                                        className={btnDanger}
                                    >
                                        <Trash2 size={13} /> Yes, delete
                                    </button>
                                    <button onClick={() => setConfirming(null)} data-testid={`admin-social-cancel-delete-${index}`} className={btnGhost}>
                                        Keep
                                    </button>
                                </>
                            ) : (
                                <button onClick={() => setConfirming(index)} data-testid={`admin-social-delete-${index}`} className={btnDanger}>
                                    <Trash2 size={13} /> Delete
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
                <button
                    onClick={() => setItems((list) => [...list, { platform: "instagram", url: "", label: "" }])}
                    data-testid="admin-social-add"
                    className={btnGhost}
                >
                    <Plus size={13} /> Add Link
                </button>
                <button onClick={() => save(items)} disabled={saving} data-testid="admin-social-save" className={btnPrimary}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    {saving ? "Saving…" : "Save Links"}
                </button>
            </div>
        </section>
    );
};

export default SocialManager;
